import { Link, Stack, Typography } from '@mui/material';
import { Bookmark } from 'components/common/Bookmark/Bookmark';
import { DynamicToolTip } from 'components/common/DynamicToolTip';
import { VerifiedCompanyIcon } from 'components/common/icon/VerifiedCompanyIcon';
import { getFoodItemInfo, getItemInfo } from 'helpers/Search';
import { MRT_ColumnDef } from 'material-react-table';
import { Link as RouterLink } from 'react-router-dom';
import { SearchDocument, SearchTarget } from 'types/Search';
import SearchCertifications from '../../SearchCertificationList/SearchCertificationList';
import { useAuth } from 'react-oidc-context';
import { useMemo } from 'react';
import { BOOKMARK_SIZE } from '../types';
import { useTranslation } from 'react-i18next';

export const useCovaloColumns = (
  isFood: boolean,
  searchTarget: SearchTarget
): MRT_ColumnDef<SearchDocument>[] => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  return useMemo(
    () => [
      ...(isAuthenticated
        ? ([
            {
              accessorKey: 'bookmarked',
              enableHiding: false,
              enableColumnOrdering: false,
              visibleInShowHideMenu: false,
              accessorFn: row => row.bookmarked,
              Cell: ({ row }) => (
                <Bookmark
                  product={row.original}
                  isBookmarked={!!row.original.bookmarked}
                />
              ),
              header: '',
              size: BOOKMARK_SIZE
            }
          ] as MRT_ColumnDef<SearchDocument>[])
        : [{ accessorKey: 'placeholder', header: '', size: BOOKMARK_SIZE }]),

      {
        accessorKey: 'name',
        enableHiding: false,
        enableColumnOrdering: false,
        visibleInShowHideMenu: false,
        header: t(`products.types.singular.${searchTarget}`),
        Cell: ({ renderedCellValue }) => (
          <Typography variant="body2" sx={{ fontWeight: '500' }}>
            {renderedCellValue}
          </Typography>
        )
      },
      {
        accessorKey: 'attributes',
        header: 'Attributes',
        enableColumnOrdering: false,
        accessorFn: row =>
          isFood ? getFoodItemInfo(row) : getItemInfo(row, false),
        enableHiding: false,
        visibleInShowHideMenu: false,
        Cell: ({ renderedCellValue }) => (
          <DynamicToolTip text={renderedCellValue} lineClamp={2} />
        )
      },
      {
        accessorKey: 'company.name',
        header: 'Company',
        enableColumnOrdering: false,
        enableHiding: false,
        visibleInShowHideMenu: false,
        accessorFn: row => row.company.name,
        Cell: ({ row, renderedCellValue }) => (
          <Stack direction={'row'} sx={{ alignItems: 'center', gap: 1 }}>
            <Link
              component={RouterLink}
              onClick={e => e.stopPropagation()}
              sx={{
                color: ({ palette }) => palette.text.primary,
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' }
              }}
              to={`/company/${row.original.company.key}`}
            >
              {renderedCellValue}
            </Link>
            {row.original.company.verified && <VerifiedCompanyIcon size="sm" />}
          </Stack>
        )
      },
      ...(isAuthenticated
        ? ([
            {
              accessorKey: 'cerfitications',
              header: 'Awards & Certifications',
              accessorFn: row =>
                row?.categories?.technical?.certifications ?? '',
              Cell: ({ row }) =>
                row?.original?.categories?.technical?.certifications && (
                  <SearchCertifications
                    certifications={
                      row.original.categories.technical.certifications
                    }
                  />
                )
            }
          ] as MRT_ColumnDef<SearchDocument>[])
        : [])
    ],
    [isAuthenticated, isFood, searchTarget, t]
  );
};
